/**
 * Tab de configuración de categorías. Las categorías ya no vienen por seed
 * — se crean aquí con grupos de texto libre (decisión 2026-05-10).
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Pencil, Archive, Trash2 } from "lucide-react";
import clsx from "clsx";
import { categoryCreateSchema, type CategoryCreateInput } from "@axorial/shared";
import { api } from "@/lib/api";

interface Category {
  id: string;
  name: string;
  kind: "ingreso" | "gasto";
  group: string | null;
  ivaApplicable: boolean;
  active: boolean;
}

export function CategoriesTab() {
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery<{ categories: Category[] }>({
    queryKey: ["finance", "categories", showAll],
    queryFn: () => api.get(`/finance/categories${showAll ? "?all=1" : ""}`),
  });

  const cats = data?.categories ?? [];
  const ingresos = cats.filter((c) => c.kind === "ingreso");
  const gastos = cats.filter((c) => c.kind === "gasto");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="cursor-pointer label gap-2 text-sm">
          <input
            type="checkbox"
            className="toggle toggle-sm toggle-primary"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
          />
          Mostrar archivadas
        </label>
        <button type="button" onClick={() => setCreating(true)} className="btn btn-primary btn-sm">
          <Plus className="size-4" /> Nueva categoría
        </button>
      </div>

      {isLoading ? (
        <div className="grid place-items-center py-12">
          <span className="loading loading-spinner text-primary" />
        </div>
      ) : cats.length === 0 ? (
        <div className="card-elevated p-8 text-center text-sm text-base-content/65">
          Sin categorías configuradas. Crea las que necesites — usa el campo <strong>Grupo</strong>{" "}
          para agruparlas en reportes (ej. "Hosting", "SaaS", "Nóminas").
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CategoryGroup title="Ingresos" kind="ingreso" items={ingresos} onEdit={setEditing} />
          <CategoryGroup title="Gastos" kind="gasto" items={gastos} onEdit={setEditing} />
        </div>
      )}

      {creating && <CategoryModal mode="create" onClose={() => setCreating(false)} />}
      {editing && <CategoryModal mode="edit" category={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function CategoryGroup({
  title,
  kind,
  items,
  onEdit,
}: {
  title: string;
  kind: "ingreso" | "gasto";
  items: Category[];
  onEdit: (c: Category) => void;
}) {
  // Sub-agrupar por `group` (texto libre, puede ser null)
  const groups: Record<string, Category[]> = {};
  for (const c of items) {
    const g = c.group ?? "Sin grupo";
    if (!groups[g]) groups[g] = [];
    groups[g].push(c);
  }
  const sortedGroups = Object.entries(groups).toSorted(([a], [b]) => a.localeCompare(b));

  return (
    <div className="card-elevated">
      <div className="px-5 pt-5 pb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wider text-base-content/70">
          {title}
        </h3>
        <span
          className={clsx("badge badge-sm", kind === "ingreso" ? "badge-success" : "badge-error")}
        >
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="px-5 pb-5 text-sm text-base-content/55 italic">
          Sin categorías de {kind}.
        </div>
      ) : (
        <div className="px-3 pb-3 space-y-3">
          {sortedGroups.map(([group, cats]) => (
            <div key={group} className="space-y-1">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-base-content/55 px-2">
                {group}
              </div>
              <ul>
                {cats.map((c) => (
                  <CategoryRow key={c.id} category={c} onEdit={() => onEdit(c)} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryRow({ category, onEdit }: { category: Category; onEdit: () => void }) {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api.del(`/finance/categories/${category.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["finance", "categories"] }),
  });

  return (
    <li
      className={clsx(
        "flex items-center gap-2 px-2 py-1.5 rounded hover:bg-base-200/60 group",
        !category.active && "opacity-55",
      )}
    >
      <span className="flex-1 text-sm truncate">{category.name}</span>
      {!category.ivaApplicable && (
        <span className="badge badge-xs badge-ghost" title="Sin IVA">
          sin IVA
        </span>
      )}
      {!category.active && <span className="badge badge-xs badge-ghost">Archivada</span>}
      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={onEdit}
          className="btn btn-ghost btn-xs btn-square"
          title="Editar"
        >
          <Pencil className="size-3" />
        </button>
        <button
          type="button"
          onClick={() => {
            if (confirm(`¿Borrar / archivar "${category.name}"?`)) remove.mutate();
          }}
          className="btn btn-ghost btn-xs btn-square text-error"
          title={category.active ? "Archivar" : "Borrar"}
        >
          {category.active ? <Archive className="size-3" /> : <Trash2 className="size-3" />}
        </button>
      </div>
    </li>
  );
}

function CategoryModal({
  mode,
  category,
  onClose,
}: {
  mode: "create" | "edit";
  category?: Category;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CategoryCreateInput>({
    resolver: zodResolver(categoryCreateSchema),
    defaultValues: category
      ? {
          name: category.name,
          kind: category.kind,
          group: category.group ?? undefined,
          ivaApplicable: category.ivaApplicable,
        }
      : { kind: "gasto", ivaApplicable: true },
  });

  // Grupos existentes filtrados por kind para el datalist del combobox.
  // Toda categoría guarda su grupo; aquí extraemos los únicos del mismo
  // kind para sugerir al usuario sin imponer (puede escribir uno nuevo).
  const allCategories = useQuery<{ categories: Category[] }>({
    queryKey: ["finance", "categories", true],
    queryFn: () => api.get("/finance/categories?all=1"),
  });
  const watchedKind = useWatch({ control, name: "kind" });
  const groupSuggestions = useMemo(() => {
    const list = allCategories.data?.categories ?? [];
    const set = new Set<string>();
    for (const c of list) {
      if (c.kind !== watchedKind) continue;
      if (c.group && c.group.trim()) set.add(c.group.trim());
    }
    return [...set].toSorted((a, b) => a.localeCompare(b, "es"));
  }, [allCategories.data, watchedKind]);

  const save = useMutation({
    mutationFn: (data: CategoryCreateInput) =>
      mode === "create"
        ? api.post("/finance/categories", data)
        : api.patch(`/finance/categories/${category!.id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["finance", "categories"] });
      onClose();
    },
  });

  return (
    <div
      className="modal modal-open"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-box max-w-md">
        <h3 className="font-display font-semibold text-lg mb-4">
          {mode === "create" ? "Nueva categoría" : "Editar categoría"}
        </h3>
        <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-3">
          <label className="form-control w-full block">
            <div className="label py-1">
              <span className="label-text text-sm font-medium">
                Nombre <span className="text-error">*</span>
              </span>
            </div>
            <input
              className="input input-bordered w-full"
              placeholder="Ej. Hosting AWS"
              autoFocus
              {...register("name")}
            />
            {errors.name && <p className="text-xs text-error mt-1.5">{errors.name.message}</p>}
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="form-control w-full block">
              <div className="label py-1">
                <span className="label-text text-sm font-medium">
                  Tipo <span className="text-error">*</span>
                </span>
              </div>
              <select className="select select-bordered w-full" {...register("kind")}>
                <option value="gasto">Gasto</option>
                <option value="ingreso">Ingreso</option>
              </select>
            </label>
            <label className="form-control w-full block">
              <div className="label py-1">
                <span className="label-text text-sm font-medium">Grupo</span>
              </div>
              <Controller
                control={control}
                name="group"
                render={({ field }) => (
                  <GroupCombobox
                    value={field.value ?? ""}
                    onChange={(v) => field.onChange(v)}
                    suggestions={groupSuggestions}
                  />
                )}
              />
              <p className="text-xs text-base-content/55 mt-1.5 leading-relaxed">
                Elige uno existente o escribe uno nuevo.
              </p>
            </label>
          </div>

          <label className="cursor-pointer label justify-start gap-3 py-1">
            <input
              type="checkbox"
              className="toggle toggle-primary toggle-sm"
              {...register("ivaApplicable")}
            />
            <div>
              <div className="text-sm font-medium">Aplica IVA (16%)</div>
              <div className="text-xs text-base-content/55 leading-relaxed">
                Desactiva para nóminas, impuestos, comisiones bancarias sin IVA.
              </div>
            </div>
          </label>

          {save.isError && (
            <div className="alert alert-error text-sm">
              {save.error instanceof Error ? save.error.message : "Error al guardar"}
            </div>
          )}

          <div className="modal-action mt-4">
            <button type="button" onClick={onClose} className="btn btn-ghost">
              Cancelar
            </button>
            <button type="submit" disabled={save.isPending} className="btn btn-primary">
              {save.isPending && <span className="loading loading-spinner loading-xs" />}
              {mode === "create" ? "Crear" : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Combobox controlado: input libre con dropdown estilizado de sugerencias.
 * Filtra mientras se escribe. Hace click en una opción la setea y cierra.
 * Reemplaza `<input list>` + `<datalist>` (que dependen del estilo nativo
 * del browser y no encajan con el design system).
 */
function GroupCombobox({
  value,
  onChange,
  suggestions,
}: {
  value: string;
  onChange: (v: string) => void;
  suggestions: string[];
}) {
  const [open, setOpen] = useState(false);
  const q = value.trim().toLowerCase();
  const filtered = q === "" ? suggestions : suggestions.filter((s) => s.toLowerCase().includes(q));
  const showDropdown = open && filtered.length > 0;

  return (
    <div className="relative">
      <input
        className="input input-bordered w-full"
        placeholder={
          suggestions.length > 0
            ? `Ej. ${suggestions.slice(0, 3).join(" · ")}`
            : "Hosting · SaaS · Nóminas"
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {showDropdown && (
        <ul className="absolute left-0 right-0 top-full mt-1 z-30 card-elevated max-h-48 overflow-y-auto p-1 text-sm">
          {filtered.map((s) => (
            <li key={s}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(s);
                  setOpen(false);
                }}
                className={clsx(
                  "w-full text-left px-3 py-1.5 rounded-md hover:bg-base-200 transition-colors",
                  value === s && "bg-primary/10 text-primary",
                )}
              >
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
